import { buildCvHtml, NIVEAUX_DENSITE, type CvMeta } from './pdf';
import type { GeneratedCv } from '../types';

/**
 * UN CV D'ALTERNANCE TIENT SUR UNE PAGE A4, TOUJOURS (décision du 10/10/2026).
 *
 * LA MESURE EST CELLE DU PDF LUI-MÊME : `expo-print` rend le nombre de pages calculé par le moteur
 * qui produit le fichier (iOS : `UIPrintPageRenderer.numberOfPages`). On ne devine pas une hauteur,
 * on lit ce qui sera exporté, et on resserre jusqu'à ce qu'il n'y ait qu'une page :
 *   1. densité 0 → 3 : interligne et espacements seulement (les polices sont déjà au plancher) ;
 *   2. une version condensée demandée UNE fois au serveur (sans quota) ;
 *   3. en dernier recours, la troncature des éléments les moins prioritaires, un cran à la fois.
 * Jamais au-dessus de la densité par défaut : un CV court garde sa taille normale.
 */

export interface PdfImprime {
  uri: string;
  numberOfPages: number;
}

export interface ResultatUnePage {
  uri: string;
  cv: GeneratedCv;
  niveau: number;
  pages: number;
  /** Le contenu vient de la version condensée par le serveur. */
  condense: boolean;
  /** Des éléments ont été retirés pour tenir sur une page. */
  tronque: boolean;
}

export interface DependancesUnePage {
  imprimer: (html: string, cv: GeneratedCv, niveau: number) => Promise<PdfImprime>;
  condenser?: (cv: GeneratedCv) => Promise<GeneratedCv | null>;
}

const NIVEAU_MAX = NIVEAUX_DENSITE - 1;
// Garde-fou : un CV ne compte jamais autant d'éléments, mais une boucle ne doit pas pouvoir tourner sans fin.
const CRANS_MAX = 60;

/**
 * Retire l'élément le MOINS prioritaire, un seul. `null` quand il ne reste rien de raisonnable à
 * retirer. Ordre : centres d'intérêt, projets (puces puis projet), dernières puces de la dernière
 * expérience (la moins pertinente : le serveur les classe par pertinence pour le poste), expériences
 * au-delà de deux, formations au-delà d'une, langues au-delà de deux, compétences au-delà de trois.
 */
export function tronquerUnCran(cv: GeneratedCv): GeneratedCv | null {
  if (cv.interests.length > 0) return { ...cv, interests: cv.interests.slice(0, -1) };

  if (cv.projects.length > 0) {
    const dernier = cv.projects[cv.projects.length - 1];
    const autres = cv.projects.slice(0, -1);
    return dernier.bullets.length > 0
      ? { ...cv, projects: [...autres, { ...dernier, bullets: dernier.bullets.slice(0, -1) }] }
      : { ...cv, projects: autres };
  }

  for (let i = cv.experiences.length - 1; i >= 0; i--) {
    const e = cv.experiences[i];
    if (e.bullets.length > 1) {
      const experiences = cv.experiences.map((x, j) => (j === i ? { ...x, bullets: x.bullets.slice(0, -1) } : x));
      return { ...cv, experiences };
    }
  }
  if (cv.experiences.length > 2) return { ...cv, experiences: cv.experiences.slice(0, -1) };
  if (cv.education.length > 1) return { ...cv, education: cv.education.slice(0, -1) };
  if (cv.languages.length > 2) return { ...cv, languages: cv.languages.slice(0, -1) };
  if (cv.skills.length > 3) return { ...cv, skills: cv.skills.slice(0, -1) };
  return null;
}

export async function ajusterUnePage(cv: GeneratedCv, meta: CvMeta, deps: DependancesUnePage): Promise<ResultatUnePage> {
  const essayerLesDensites = async (c: GeneratedCv): Promise<{ pdf: PdfImprime; niveau: number }> => {
    let dernier: { pdf: PdfImprime; niveau: number } | null = null;
    for (let niveau = 0; niveau <= NIVEAU_MAX; niveau++) {
      const pdf = await deps.imprimer(buildCvHtml(c, meta, niveau), c, niveau);
      dernier = { pdf, niveau };
      if (pdf.numberOfPages <= 1) return dernier;
    }
    return dernier!;
  };
  const resultat = (c: GeneratedCv, r: { pdf: PdfImprime; niveau: number }, condense: boolean, tronque: boolean) => ({
    uri: r.pdf.uri,
    cv: c,
    niveau: r.niveau,
    pages: r.pdf.numberOfPages,
    condense,
    tronque,
  });

  let courant = cv;
  let essai = await essayerLesDensites(courant);
  if (essai.pdf.numberOfPages <= 1) return resultat(courant, essai, false, false);

  let condense = false;
  if (deps.condenser) {
    let version: GeneratedCv | null = null;
    try {
      version = await deps.condenser(courant);
    } catch {
      version = null;
    }
    if (version) {
      courant = version;
      condense = true;
      essai = await essayerLesDensites(courant);
      if (essai.pdf.numberOfPages <= 1) return resultat(courant, essai, true, false);
    }
  }

  for (let cran = 0; cran < CRANS_MAX; cran++) {
    const suivant = tronquerUnCran(courant);
    if (!suivant) break;
    courant = suivant;
    const pdf = await deps.imprimer(buildCvHtml(courant, meta, NIVEAU_MAX), courant, NIVEAU_MAX);
    essai = { pdf, niveau: NIVEAU_MAX };
    if (pdf.numberOfPages <= 1) break;
  }
  return resultat(courant, essai, condense, true);
}
